import { Component, SimpleChanges } from '@angular/core';
import { ComponentModalBase } from '@framework/base';
import { AuthService } from '@framework/services';
import { CompanyHttpService } from '@modules/story/services';
import { getAuthorizationHeader } from '@modules/shared/utils/auth-header.util';
import { PROMPT_AUDIENCE_OPTIONS, PromptAudience, parsePromptActive, parsePromptAudience, promptKey } from '@modules/shared/utils/prompt-audience.util';
import { ToastrService } from 'ngx-toastr';

export interface UploadPromptsCsvModalData {
  companyId: number;
}

interface CsvPreview {
  rows: string[][];
  // File line each row starts on
  lines?: number[];
  total_rows: number;
  column_count: number;
  suggested: {
    has_header: boolean;
    prompt_column: number;
    audience_column: number | null;
    active_column: number | null;
  };
}

interface UploadPromptsCsvResult {
  created: number;
  updated: number;
  unchanged: number;
  skipped: { row: number; prompt: string; reason: string }[];
}

type Step = 'pick' | 'map' | 'done';

interface RowCheck {
  line: number;
  prompt: string;
  audience: string;
  audienceIsDefault: boolean;
  active: string;
  // Why the server will skip this row, or '' when it will be imported
  problem: string;
}

interface MappingCheck {
  rows: RowCheck[];
  importable: number;
  skipped: RowCheck[];
}

const NOT_MAPPED = '';
const PREVIEW_DATA_ROWS = 5;
// t_story_questions.question_text is varchar(255)
const MAX_PROMPT_LENGTH = 255;

@Component({
  selector: 'app-upload-prompts-csv-modal',
  templateUrl: './upload-prompts-csv-modal.component.html',
  styleUrls: [
    '../upload-tags-csv-modal/upload-tags-csv-modal.component.css',
    './upload-prompts-csv-modal.component.css'
  ]
})
export class UploadPromptsCsvModalComponent extends ComponentModalBase<UploadPromptsCsvModalData> {
  public step: Step = 'pick';
  public isBusy = false;
  public isDragging = false;
  public file: File = null;
  public preview: CsvPreview = null;
  public result: UploadPromptsCsvResult = null;
  // Stays true once any import in this modal saved something, so the list reloads on close
  // even after "Import another file".
  private importedAny = false;
  private checkKey = '';
  private checkCache: MappingCheck = null;
  public audienceOptions = PROMPT_AUDIENCE_OPTIONS;
  public readonly notMapped = NOT_MAPPED;

  // Column indexes as strings so they bind straight to <select>; '' means not mapped
  public hasHeader = true;
  public promptColumn = NOT_MAPPED;
  public audienceColumn = NOT_MAPPED;
  public activeColumn = NOT_MAPPED;
  public defaultAudience: PromptAudience = 'both';

  constructor(
    private companyHttpService: CompanyHttpService,
    private authService: AuthService,
    private toastService: ToastrService
  ) {
    super();
  }

  protected onInit(): void {

  }

  protected onDestroy(): void {

  }

  protected onChanges(changes: SimpleChanges): void {

  }

  protected onDoCheck(): void {

  }

  protected onAfterContentInit(): void {

  }

  protected onAfterContentChecked(): void {

  }

  protected onAfterViewInit(): void {

  }

  protected onAfterViewChecked(): void {

  }

  get changedAny(): boolean {
    return this.importedAny;
  }

  get columns(): { value: string; label: string }[] {
    if (!this.preview) {
      return [];
    }
    const first = this.preview.rows[0] || [];
    const columns = [];
    for (let i = 0; i < this.preview.column_count; i++) {
      const letter = this.columnLetter(i);
      const header = this.hasHeader ? (first[i] || '').trim() : '';
      const sample = this.dataRows.map((row) => (row[i] || '').trim()).find((value) => !!value) || '';
      let label = header ? `${header} (column ${letter})` : `Column ${letter}`;
      if (!header && sample) {
        label += ` — e.g. "${this.truncate(sample, 40)}"`;
      }
      columns.push({ value: String(i), label });
    }
    return columns;
  }

  get dataRows(): string[][] {
    if (!this.preview) {
      return [];
    }
    return this.preview.rows.slice(this.hasHeader ? 1 : 0);
  }

  // Checks every row with the server's rules, so the counts and warnings match what the import will do
  get check(): MappingCheck {
    const key = [this.hasHeader, this.promptColumn, this.audienceColumn, this.activeColumn, this.defaultAudience,
      this.preview ? this.preview.rows.length : 0].join('|');
    if (this.checkCache && key === this.checkKey) {
      return this.checkCache;
    }
    const defaultLabel = this.audienceLabel(this.defaultAudience);
    const offset = this.hasHeader ? 1 : 0;
    const lines = (this.preview && this.preview.lines) || [];
    const keys = new Set<string>();
    const rows = this.dataRows.map((row, index) => {
      const prompt = this.cell(row, this.promptColumn).split(/\s+/).filter(Boolean).join(' ');
      const audienceCell = this.cell(row, this.audienceColumn);
      const activeCell = this.cell(row, this.activeColumn);
      const audience = audienceCell ? parsePromptAudience(audienceCell) : this.defaultAudience;
      const active = activeCell ? parsePromptActive(activeCell) : null;
      let problem = '';
      if (!prompt) {
        problem = 'No prompt in this row.';
      } else if (prompt.length > MAX_PROMPT_LENGTH) {
        problem = `Longer than ${MAX_PROMPT_LENGTH} characters.`;
      } else if (!audience) {
        problem = `Audience "${this.truncate(audienceCell, 30)}" isn't Users, Guests, or Both.`;
      } else if (activeCell && active === null) {
        problem = `Active "${this.truncate(activeCell, 30)}" isn't Yes or No.`;
      }
      if (!problem) {
        keys.add(promptKey(prompt));
      }
      return {
        line: lines[index + offset] || index + offset + 1,
        prompt,
        audience: audienceCell ? (audience ? this.audienceLabel(audience) : audienceCell) : defaultLabel,
        audienceIsDefault: !audienceCell,
        active: activeCell ? (active === null ? activeCell : (active ? 'Yes' : 'No')) : '—',
        problem
      };
    });
    this.checkKey = key;
    this.checkCache = { rows, importable: keys.size, skipped: rows.filter((row) => !!row.problem) };
    return this.checkCache;
  }

  get previewRows(): RowCheck[] {
    return this.check.rows.slice(0, PREVIEW_DATA_ROWS);
  }

  get promptCount(): number {
    return this.preview ? this.check.importable : 0;
  }

  get mappingError(): string {
    if (this.promptColumn === NOT_MAPPED) {
      return 'Choose the column that holds your prompts.';
    }
    const used = [this.promptColumn, this.audienceColumn, this.activeColumn].filter((value) => value !== NOT_MAPPED);
    if (new Set(used).size !== used.length) {
      return 'Each column can only be used once.';
    }
    if (!this.dataRows.length) {
      return 'There are no prompts below the header row.';
    }
    if (!this.promptCount) {
      return 'None of these rows can be imported. Check the rows listed above.';
    }
    return '';
  }

  public trackByValue(index: number, column: { value: string }) {
    return column.value;
  }

  public close() {
    // An import that's still running would finish unseen, so wait for it
    if (this.isBusy) {
      return;
    }
    this.onClose.emit(this.changedAny);
  }

  public startOver() {
    if (this.isBusy) {
      return;
    }
    this.step = 'pick';
    this.file = null;
    this.preview = null;
    this.result = null;
  }

  public onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  public onDragLeave() {
    this.isDragging = false;
  }

  public onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
    const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
    if (file) {
      this.loadFile(file);
    }
  }

  public onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = '';
    if (file) {
      this.loadFile(file);
    }
  }

  private loadFile(file: File) {
    if (this.isBusy) {
      return;
    }
    const isCsv = /\.csv$/i.test(file.name) || file.type === 'text/csv' || file.type === 'application/vnd.ms-excel';
    if (!isCsv) {
      this.toastService.error('Upload a CSV file.');
      return;
    }
    this.isBusy = true;
    getAuthorizationHeader(this.authService).then((authorization) => {
      this.companyHttpService.previewStoryQuestionsCsv(this.initialState.companyId, file, authorization).subscribe((preview: CsvPreview) => {
        this.isBusy = false;
        this.file = file;
        this.preview = preview;
        this.hasHeader = preview.suggested.has_header;
        this.promptColumn = this.toColumn(preview.suggested.prompt_column);
        this.audienceColumn = this.toColumn(preview.suggested.audience_column);
        this.activeColumn = this.toColumn(preview.suggested.active_column);
        this.step = 'map';
      }, (error) => {
        this.isBusy = false;
        this.toastService.error(this.errorMessage(error, 'Could not read that file.'));
      });
    }).catch(() => {
      this.isBusy = false;
      this.toastService.error('Could not read that file.');
    });
  }

  public importPrompts() {
    if (this.isBusy || this.mappingError) {
      return;
    }
    const mapping = {
      has_header: this.hasHeader ? 'true' : 'false',
      prompt_column: this.promptColumn,
      audience_column: this.audienceColumn,
      active_column: this.activeColumn,
      default_audience: this.defaultAudience
    };
    this.isBusy = true;
    getAuthorizationHeader(this.authService).then((authorization) => {
      this.companyHttpService.bulkUploadStoryQuestions(this.initialState.companyId, this.file, mapping, authorization).subscribe((response: any) => {
        this.isBusy = false;
        this.result = {
          created: response?.created || 0,
          updated: response?.updated || 0,
          unchanged: response?.unchanged || 0,
          skipped: response?.skipped || []
        };
        if (this.result.created || this.result.updated) {
          this.importedAny = true;
        }
        this.step = 'done';
      }, (error) => {
        this.isBusy = false;
        this.toastService.error(this.errorMessage(error, 'Could not import prompts.'));
      });
    }).catch(() => {
      this.isBusy = false;
      this.toastService.error('Could not import prompts.');
    });
  }

  private audienceLabel(audience: PromptAudience): string {
    const option = this.audienceOptions.find((item) => item.value === audience);
    return option ? option.label : '';
  }

  private cell(row: string[], column: string): string {
    if (column === NOT_MAPPED) {
      return '';
    }
    return (row[Number(column)] || '').trim();
  }

  private toColumn(value: number | null): string {
    return value === null || value === undefined ? NOT_MAPPED : String(value);
  }

  private columnLetter(index: number): string {
    let letter = '';
    let n = index + 1;
    while (n > 0) {
      const rem = (n - 1) % 26;
      letter = String.fromCharCode(65 + rem) + letter;
      n = Math.floor((n - 1) / 26);
    }
    return letter;
  }

  private truncate(value: string, length: number): string {
    return value.length > length ? value.slice(0, length - 1) + '…' : value;
  }

  private errorMessage(error: any, fallback: string): string {
    return error?.error?.message || error?.error?.detail || fallback;
  }
}
